import { HotKeyManager }         from "@/helpers/hot.key.manager";

import { Logger }                from "@/util/logger";

import { ServiceManager }        from "@/services/service.manager";

import { LineSimJobElement }     from "@/models/project/line.sim.job.element";
import { BlobElement }           from "@/models/project/blob.element";
import { ProjectWindow }         from "@/screens/ide/project.window";

import { DocumentWindowManager } from "@/screens/ide/document.window.manager";
import { Window }                from '@/screens/ide/window';

import KeyDownEvent = JQuery.KeyDownEvent;
import {ActionType} from "@/models/action";
import {ActionService} from "@/services/action.service";
import {ServiceLocator} from "@/services/service.locator";
import {LineSimElement} from "@/models/project/line.sim.element";

/*
 * our project manager key bindings:
 *
 *    CTRL-L       - stringline simulator
 *    CTRL-B       - scenario browser
 *    CTRL-E       - work blocks editor
 *    CTRL-D       - diversions editor
 *    CTRL-O       - TSO editor
 *    ALT-K        - generate KPI table (with maybe auto-edit on the KPI table)
 *    CTRL-[SPACE] - launch simulation
 *    CTRL-S       - save current editor
 *
 * Note: we can not use CTRL-W/T/N, they are reserved by browsers, you can't override them.
 *
 * https://unixpapa.com/js/key.html
 *
 */

/**
 *
 * @class IDEHotKeys
 *
 * @brief handle hot key actions specifically for the IDE screen
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      August 2020
 *
 */
export class IDEHotKeys extends HotKeyManager {

  protected actions: ActionService;

  /** the project window where we can find the project manager tree */
  private projectWindow: ProjectWindow = null;

  /** the "tab" manager for active editors etc. */
  private docManager: DocumentWindowManager = null;

  /** standard constructor */
  constructor(projectWin: ProjectWindow, docManager: DocumentWindowManager, services: ServiceManager) {

    super('app-root', services);

    this.projectWindow = projectWin;
    this.docManager    = docManager;

    this.actions = ServiceLocator.injector.get(ActionService);

    $(document).keydown( (e: KeyDownEvent) => {

      let bubble = true;

      if (e.key.toLowerCase() == 'f5') {

        bubble = this.f5();
      }

      if (e.ctrlKey || e.altKey || e.shiftKey) {

        if (e.altKey) {

          bubble = this.altKey(e.key);
        }

        if (e.ctrlKey) {

          bubble = this.ctrlKey(e.key);
        }

        if (e.shiftKey) {

          bubble = this.shiftKey(e.key);
        }

      } else {

        if (e.key.toLowerCase() === 'enter') {

          bubble = this.enter();
        }

        if (e.key.toLowerCase() === 'tab') {

          bubble = this.tab();
        }

        if (e.key.toLowerCase() === 'escape') {

          bubble = this.escape();
        }
      }

      if (!bubble) {

        e.preventDefault();
        e.stopPropagation();

        return false;
      }

      return bubble;

    });
  }

  public f5(): boolean {

    Logger.debug('[IDEHotKeys] (F5) launching simulation...');

    this.actions.action(ActionType.Window_SimpleSim).execute();

    return false;
  }

  /** handle ALT+{key} */
  public altKey(key: string): boolean {

    let result: LineSimJobElement|LineSimElement = this.projectWindow.selectedResult();
    let   blob: BlobElement                      = this.projectWindow.selectedScenario();

    if (typeof result === 'undefined') {

      result = null;
    }

    if (typeof blob === 'undefined') {

      blob = null;
    }

    if (result) {

      if (key.toLowerCase() === 'k') {

        Logger.debug('[IDEHotKeys] running KPI...', result);

        this.projectWindow.project().generateKPITable(result);

      }
    }

    /* check if we *would* have done something */
    if (blob === null) {

      if (['k'].includes(key)) {

        /* we *would* have done something */

        Logger.debug(`[IDEHotKeys] skipping ALT-${key}`);
        return false;
      }
    }

    if (result === null) {

      if (['k'].includes(key)) {

        /* we *would* have done something */

        Logger.debug(`[IDEHotKeys] skipping ALT-${key}`);
        return false;
      }
    }

    return true;
  }

  /** handle CTRL+{key} */
  public ctrlKey(key: string): boolean {

    let result: LineSimJobElement|LineSimElement = this.projectWindow.selectedResult();
    let   blob: BlobElement                      = this.projectWindow.selectedScenario();

    if (typeof result === 'undefined') {

      result = null;
    }

    if (typeof blob === 'undefined') {

      blob = null;
    }

    key = key.toLowerCase();

    if (key === 's') {

      let active: Window;

      /* find the active window */

      active = this.docManager.activeTab();

      Logger.debug('[IDEHotKeys] saving: ', active);

      if (active === null) {

        /* nothing to do */

        return false;
      }

      /* tell the window to save itself */

      active.save();

      return false;
    }

    if (key === ' ') {

      Logger.debug('[IDEHotKeys] launching simulation...');

      this.actions.action(ActionType.Window_SimpleSim).execute();

      return false;
    }

    if (result !== null) {

      if (key === 'l') {

        Logger.debug('[IDEHotKeys] launching string line viewer...', result);

        result.action('viewstringline', [result]);

        return false;
      }
    }

    if (blob !== null) {

      if (key === 'b') {

        Logger.debug('[IDEHotKeys] launching scenario browser...', blob);

        blob.action('scenariobrowseblob', [blob]);

        return false;

      } else if (key === 'e') {

        Logger.debug('[IDEHotKeys] launching workblox...', blob);

        blob.action('workbloxedit', [blob]);

        return false;

      } else if (key === 'd') {

        Logger.debug('[IDEHotKeys] launching diversions...', blob);

        blob.action('diversionsedit', [blob]);

        return false;

      } else if (key === 'o') {

        Logger.debug('[IDEHotKeys] launching TSO...', blob);

        blob.action('tsoedit', [blob]);

        return false;
      }

      if (key.toLowerCase() === 'k') {

        Logger.debug('[IDEHotKeys] running KPI...', blob);

        this.projectWindow.project().generateKPITable(blob);

        return false;
      }
    }

    /* check if we *would* have done something */

    if (blob === null) {

      if (['b','d', 'e', 'o', 'k'].includes(key)) {

        /* we *would* have done something */

        Logger.debug(`[IDEHotKeys] skipping CTRL-${key}`);
        return false;
      }
    }

    if (result === null) {

      if (['l'].includes(key)) {

        /* we *would* have done something */

        Logger.debug(`[IDEHotKeys] skipping CTRL-${key}`);
        return false;
      }
    }

    return true;
  }

}
